import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Container,
  Button,
} from "@mui/material";
import Background from "../../components/Background";
import Sidebar from "../../components/Sidebar";
import Mobilesidebar from "../../components/Mobilesidebar";
import MenteeHeader from "../../components/MenteeHeader";

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { baseFetch } from "../../helpers/apiClient";
import { useSnackbar } from "../../context/SnackbarContext";

const STRIPE_INPUT_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      padding: "10px",
      color: "#2A4876",
      "::placeholder": { color: "#9DB4C0" },
    },
    invalid: {
      color: "#e53935",
    },
  },
};

function PaymentDetailsForm() {
  const stripe = useStripe();
  const elements = useElements();

  const [cardHolder, setCardHolder] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    if (!stripe || !elements) {
      setError("Stripe is not fully loaded");
      setLoading(false);
      return;
    }

    // 1️⃣ Create Payment Method
    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
      billing_details: { name: cardHolder },
    });
    console.log(paymentMethod, "paymentMethod");

    if (pmError) {
      setError(pmError.message);
      setLoading(false);
      return;
    }

    try {
      const resp = await baseFetch({
        url: "/v1/web/subscription/updatePaymentMethod",
        method: "POST",
        body: {
          paymentMethodId: paymentMethod?.id,
        },
      });
      showSnackbar({ message: resp?.data?.message, severity: "success" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }

    setLoading(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Card Holder Name */}
      <Box>
        <Typography
          sx={{
            marginBottom: 1,
            color: "#2A4876",
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          Card Holder's Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="John Doe"
          value={cardHolder}
          onChange={(e) => setCardHolder(e.target.value)}
          sx={{
            backgroundColor: "#F2F7FB",
            borderRadius: "24px",
            "& .MuiOutlinedInput-root": { borderRadius: "24px" },
          }}
        />
      </Box>

      {/* Card Number */}
      <Box>
        <Typography
          sx={{
            marginBottom: 1,
            color: "#2A4876",
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          Card Number
        </Typography>
        <Box
          sx={{
            padding: "12px",
            backgroundColor: "#F2F7FB",
            borderRadius: "24px",
            border: "1px solid #E0E0E0",
          }}
        >
          <CardNumberElement options={STRIPE_INPUT_STYLE} />
        </Box>
      </Box>

      {/* Expiry + CVC */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              marginBottom: 1,
              color: "#2A4876",
              fontWeight: 500,
              textAlign: "left",
            }}
          >
            Expiry Date
          </Typography>
          <Box
            sx={{
              padding: "12px",
              backgroundColor: "#F2F7FB",
              borderRadius: "24px",
              border: "1px solid #E0E0E0",
            }}
          >
            <CardExpiryElement options={STRIPE_INPUT_STYLE} />
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              marginBottom: 1,
              color: "#2A4876",
              fontWeight: 500,
              textAlign: "left",
            }}
          >
            CVC
          </Typography>
          <Box
            sx={{
              padding: "12px",
              backgroundColor: "#F2F7FB",
              borderRadius: "24px",
              border: "1px solid #E0E0E0",
            }}
          >
            <CardCvcElement options={STRIPE_INPUT_STYLE} />
          </Box>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: "red", fontSize: "14px" }}>{error}</Typography>
      )}

      {/* Submit Button */}
      <Button
        variant="contained"
        sx={{
          marginTop: 2,
          borderRadius: "24px",
          backgroundColor: "#2A4876",
          padding: "10px 20px",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 600,
          "&:hover": { backgroundColor: "#1e3553" },
        }}
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Processing…" : "Update"}
      </Button>
    </Box>
  );
}

function PaymentDetails() {
  return (
    <>
      <Background>
        <MenteeHeader />

        <Box
          sx={{
            padding: {
              xs: "10px 0 20px",
              sm: "10px 0 20px",
              md: "10px 0 20px",
              lg: "10px 0 20px",
              xl: "20px 0 30px",
            },
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <Mobilesidebar />
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={8} md={9} lg={9}>
                <Box
                  sx={{
                    height: "80vh",
                    padding: { xs: "15px", sm: "24px" },
                    borderRadius: "12px",
                    backgroundColor: "white",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "left",
                          fontWeight: "bold",
                          marginBottom: "16px",
                          color: "#2A4876",
                        }}
                      >
                        Payment Details
                      </Typography>

                      <PaymentDetailsForm />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Background>
    </>
  );
}

export default PaymentDetails;
